import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
    JobFulfillmentTypeLookup,
    JobFulfillmentTypeSelections,
    JobFulfillmentTypeSetting
} from '../../models/job-fulfillment-type-models';
import { MatDialog } from '@angular/material/dialog';
import { JobFulfillmentTypeSelectorConfirmationComponent } from './job-fulfillment-type-selector-confirmation.component';
import { mergeArrays } from '../../utilities/merge-arrays';
import { JobFulfillmentType, JobFulfillmentTypes } from '../../models/enums/job-fulfillment-type.enum';

@Component({
    selector: 'nova-job-fulfillment-type-selector',
    templateUrl: './job-fulfillment-type-selector.component.html',
    styleUrls: ['./job-fulfillment-type-selector.component.scss']
})
export class JobFulfillmentTypeSelectorComponent implements OnInit, OnChanges {
    @Input() canEdit = false;
    @Input() confirmBeforeSave = false;
    @Input() confirmTitle: string;
    @Input() confirmMessage: string;
    @Input() jobFulfillmentTypeSelections: JobFulfillmentTypeSelections;
    @Input() jobFulfillmentTypeExclusions: number[] = [];
    @Input() allowEditWithoutAvailableFulfillmentTypes = false;

    @Output() selectionsChanged = new EventEmitter<JobFulfillmentTypeSetting[]>();

    selectedTypesControl: FormControl<number[]>;
    defaultTypeControl: FormControl<number>;
    editMode = false;
    hasChanges = false;
    JobFulfillmentTypeEnum = JobFulfillmentType;
    JobFulfillmentTypesList = JobFulfillmentTypes;
    originalJobFulfillmentTypeSelections: JobFulfillmentTypeSelections;
    availableTypes: JobFulfillmentTypeLookup[] = [];

    constructor(private readonly dialog: MatDialog) {}

    get viewModeText() {
        return this.selectedTypeIds
            .sort((a, b) => ((b === this.defaultTypeId) as any) - ((a === this.defaultTypeId) as any))
            .map((id) => {
                const lookup = this.availableTypes.find((a) => a.id === id);
                let text;
                if (lookup) {
                    text = id === this.defaultTypeId ? `${lookup.name} (Default)` : lookup.name;
                }
                return text;
            })
            .filter((x) => !!x)
            .join(', ');
    }

    get selectionsTriggerText(): string {
        return this.availableTypes
            .filter((x) => this.selectedTypesControl.value.includes(x.id))
            .map((x) => x.name)
            .join(', ');
    }

    get defaultTriggerText(): string {
        return this.availableTypes.find((x) => x.id === this.defaultTypeControl.value)?.name;
    }

    get availableDefaultTypes(): JobFulfillmentTypeLookup[] {
        return this.availableTypes.filter((x) => this.selectedTypesControl.value.includes(x.id));
    }

    get saveButtonDisabled(): boolean {
        return (
            !this.hasChanges ||
            this.selectedTypesControl.hasError('required') ||
            this.defaultTypeControl.hasError('required')
        );
    }

    private get selectedTypeIds(): number[] {
        return this.jobFulfillmentTypeSelections?.selected ?? [];
    }

    private get defaultTypeId(): number {
        return this.jobFulfillmentTypeSelections?.default;
    }

    ngOnInit(): void {
        this.initControls();
    }

    initControls() {
        this.availableTypes = this.JobFulfillmentTypesList.filter(
            (x) => !this.jobFulfillmentTypeExclusions.includes(x.id)
        ).map<JobFulfillmentTypeLookup>((x) => ({ ...x }));

        this.originalJobFulfillmentTypeSelections = { ...this.jobFulfillmentTypeSelections };
        this.selectedTypesControl = new FormControl<number[]>(this.selectedTypeIds, Validators.required);
        this.defaultTypeControl = new FormControl<number>(this.defaultTypeId, Validators.required);
    }

    ngOnChanges(changes: SimpleChanges) {
        const jobFulfillmentTypeSelectionsChanges = changes?.jobFulfillmentTypeSelections;
        const jobFulfillmentTypeExclusionsChanges = changes?.jobFulfillmentTypeExclusions;

        if (
            (jobFulfillmentTypeSelectionsChanges &&
                jobFulfillmentTypeSelectionsChanges.currentValue !==
                    jobFulfillmentTypeSelectionsChanges.previousValue) ||
            (jobFulfillmentTypeExclusionsChanges &&
                jobFulfillmentTypeExclusionsChanges.currentValue !== jobFulfillmentTypeExclusionsChanges.previousValue)
        ) {
            this.initControls();
        }
    }

    selectionsChangedEvent() {
        if (!this.selectedTypesControl.value.includes(this.defaultTypeControl.value)) {
            this.defaultTypeControl.setValue(undefined);
            this.defaultTypeControl.markAsTouched();
        }

        this.hasChanges = true;
    }

    defaultSelectionChangedEvent() {
        this.hasChanges = true;
    }

    saveChanges() {
        if (this.confirmBeforeSave) {
            const confirmDlgRef = this.dialog.open(JobFulfillmentTypeSelectorConfirmationComponent, {
                data: {
                    title: this.confirmTitle,
                    message: this.confirmMessage
                },
                maxWidth: '25vw',
                panelClass: 'nova__dialog'
            });

            confirmDlgRef.afterClosed().subscribe((confirmed) => {
                if (confirmed) {
                    this._saveChanges();
                }
            });
        } else {
            this._saveChanges();
        }
    }

    cancelEditMode() {
        this.selectedTypesControl.setValue(this.selectedTypeIds);
        this.defaultTypeControl.setValue(this.defaultTypeId);
        this.editMode = false;
        this.hasChanges = false;
    }

    private _saveChanges() {
        const selectedTypesIds = this.selectedTypesControl.value;
        this.jobFulfillmentTypeSelections.selected = this.availableTypes
            .filter((x) => selectedTypesIds.includes(x.id))
            .map((x) => x.id);

        const defaultTypeId = this.defaultTypeControl.value;
        this.jobFulfillmentTypeSelections.default = this.availableTypes.find((x) => x.id === defaultTypeId).id;

        const selections = this.jobFulfillmentTypeSelections;
        const combinedIds = mergeArrays(
            this.originalJobFulfillmentTypeSelections.selected,
            this.jobFulfillmentTypeSelections.selected
        );
        const typesToUpsert: JobFulfillmentTypeSetting[] = [];

        // we need to make sure the list of data we send contains all possible types

        // add selected
        for (const selectedId of combinedIds) {
            typesToUpsert.push({
                jobFulfillmentTypeId: selectedId,
                isActive: selections.selected.some((x) => x === selectedId),
                isDefault: selectedId === selections.default
            });
        }

        // add unselected
        for (const algoId of JobFulfillmentTypes.map((x) => x.id)) {
            if (!combinedIds.includes(algoId)) {
                typesToUpsert.push({
                    jobFulfillmentTypeId: algoId,
                    isActive: false,
                    isDefault: false
                });
            }
        }

        this.originalJobFulfillmentTypeSelections = { ...this.jobFulfillmentTypeSelections };

        this.selectionsChanged.emit(typesToUpsert);
        this.editMode = false;
    }

    onEditClicked() {
        this.editMode = true;
        if (this.allowEditWithoutAvailableFulfillmentTypes && this.selectedTypeIds?.length === 0) {
            if (
                this.jobFulfillmentTypeSelections?.selected !== undefined &&
                this.jobFulfillmentTypeSelections?.selected !== null
            ) {
                this.jobFulfillmentTypeSelections?.selected.push(1);
            }
        }
    }
}
